import { useState } from 'react';
import { validateHyperlink } from '../tool';
import addAnswer from '../services/answerService';
import useUserContext from './useUserContext';
import { Answer } from '../types/types';

/**
 * Custom hook for managing answer submission in a dialog
 *
 * @returns text - the current text input for the answer
 * @returns textErr - the error message related to the text input
 * @returns setText - the function to update the answer text input
 * @returns postAnswer - the function to submit the answer after validation
 * @returns isSubmitting - whether the answer is currently being submitted
 * @returns isSuccess - whether the answer was successfully submitted
 * @returns resetForm - function to reset the form state
 */
const useAnswerDialog = (questionID: string) => {
  const { user } = useUserContext();
  const [text, setText] = useState<string>('');
  const [textErr, setTextErr] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  /**
   * Function to reset the form state
   */
  const resetForm = () => {
    setText('');
    setTextErr('');
    setIsSuccess(false);
  };

  /**
   * Function to post an answer to a question.
   * It validates the answer text and posts the answer if it is valid.
   */
  const postAnswer = async (): Promise<boolean> => {
    let isValid = true;
    setIsSubmitting(true);
    setIsSuccess(false);

    if (!text) {
      setTextErr('Answer text cannot be empty');
      isValid = false;
    }

    // Hyperlink validation
    if (!validateHyperlink(text)) {
      setTextErr('Invalid hyperlink format.');
      isValid = false;
    }

    if (!isValid) {
      setIsSubmitting(false);
      return false;
    }

    const answer: Answer = {
      text,
      ansBy: user.username,
      ansDateTime: new Date(),
      comments: [],
    };

    try {
      const res = await addAnswer(questionID, answer);
      if (res && res._id) {
        setIsSuccess(true);
        resetForm();
        return true;
      }
      return false;
    } catch (error) {
      setTextErr('Failed to submit answer. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    text,
    textErr,
    setText,
    postAnswer,
    isSubmitting,
    isSuccess,
    resetForm,
  };
};

export default useAnswerDialog;
